// In-memory Supabase replacement for local development.
//
// `vite dev` serves a mock SupabaseClient backed by an in-memory database that
// is seeded with sample content on first run and persisted to IndexedDB (via
// StorageService) so edits survive reloads. It emulates the small PostgREST
// query surface the app uses, plus the RLS rules from
// supabase/migrations/0001_init.sql, so the hub, studios, and loader behave
// the same as against a real project — with zero network traffic.
//
// Enabled whenever `import.meta.env.DEV` is true. Opt out of the mock (and
// point the dev server at a real backend) with VITE_SUPABASE_MOCK=0.

import type { SupabaseClient, User } from "@supabase/supabase-js";
import { compileStory } from "@/story/compiler";
import { starterModel } from "@/editor/draft";
import { StorageService } from "@/persistence/StorageService";
import type { StorySchema } from "@/story/model";

const MOCK_USER_ID = "00000000-0000-0000-0000-000000000001";
const SEED_VERSION = 1;
const STORAGE_KEY = `dev.supabase.mock.v${SEED_VERSION}`;

export function isMockEnabled(): boolean {
  if (import.meta.env.DEV !== true) return false;
  return import.meta.env.VITE_SUPABASE_MOCK !== "0";
}

type TableName = "profiles" | "characters" | "stories" | "story_characters";
type MockRow = Record<string, unknown>;

interface MockError {
  message: string;
  details: string;
  hint: string;
  code: string;
}

interface SupabaseResponse {
  data: MockRow[] | MockRow | null;
  error: MockError | null;
}

type RowFilter = (r: MockRow) => boolean;

type WriteOp =
  | { kind: "insert"; rows: MockRow[] }
  | {
      kind: "upsert";
      rows: MockRow[];
      onConflict: string;
      ignoreDuplicates: boolean;
    }
  | { kind: "update"; patch: MockRow }
  | { kind: "delete" };

function mockError(message: string): MockError {
  return {
    message,
    details: "",
    hint: "DevAdventure2K in-memory mock",
    code: "MOCK",
  };
}

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `mock-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function now(): string {
  return new Date().toISOString();
}

function combineFilters(...filters: RowFilter[]): RowFilter {
  return (r) => filters.every((f) => f(r));
}

function compareRows(
  a: MockRow,
  b: MockRow,
  col: string,
  desc: boolean
): number {
  const av = a[col];
  const bv = b[col];
  if (av === bv) return 0;
  if (av === undefined || av === null) return desc ? -1 : 1;
  if (bv === undefined || bv === null) return desc ? 1 : -1;
  const cmp = av < bv ? -1 : av > bv ? 1 : 0;
  return desc ? -cmp : cmp;
}

function parseSelect(spec: string): {
  cols: string[];
  embeds: { alias: string; table: string; cols: string }[];
} {
  const cols: string[] = [];
  const embeds: { alias: string; table: string; cols: string }[] = [];
  for (const raw of spec.split(",")) {
    const part = raw.trim();
    if (part.length === 0) continue;
    const aliased = part.match(/^([\w]+):\s*(\w+)\((.*)\)$/);
    if (aliased && aliased[1] && aliased[2]) {
      embeds.push({
        alias: aliased[1],
        table: aliased[2],
        cols: aliased[3]?.trim() || "*",
      });
      continue;
    }
    const embedded = part.match(/^(\w+)\((.*)\)$/);
    if (embedded && embedded[1]) {
      embeds.push({
        alias: embedded[1],
        table: embedded[1],
        cols: embedded[2]?.trim() || "*",
      });
      continue;
    }
    for (const c of part.split(/\s+/)) if (c) cols.push(c);
  }
  return { cols, embeds };
}

function pick(row: MockRow, cols: string): MockRow {
  if (cols === "*") return { ...row };
  const out: MockRow = {};
  for (const raw of cols.split(",")) {
    const c = raw.trim();
    if (c && c in row) out[c] = row[c];
  }
  return out;
}

function resolveEmbed(
  row: MockRow,
  embed: { table: string; cols: string },
  db: MockDatabase
): MockRow | null {
  if (embed.table === "profiles") {
    const ref = row.author_id as string | undefined;
    const target = ref
      ? db.tables.profiles.find((p) => p.id === ref)
      : undefined;
    return target ? pick(target, embed.cols) : null;
  }
  if (embed.table === "characters") {
    const ref = row.character_id as string | undefined;
    const target = ref
      ? db.tables.characters.find((c) => c.id === ref)
      : undefined;
    return target ? pick(target, embed.cols) : null;
  }
  return null;
}

class MockDatabase {
  tables: Record<TableName, MockRow[]> = {
    profiles: [],
    characters: [],
    stories: [],
    story_characters: [],
  };

  constructor(private readonly storage: StorageService) {}

  async load(): Promise<void> {
    const stored =
      await this.storage.get<Record<TableName, MockRow[]>>(STORAGE_KEY);
    if (stored) {
      const tables: TableName[] = [
        "profiles",
        "characters",
        "stories",
        "story_characters",
      ];
      for (const table of tables) {
        const rows = stored[table];
        if (Array.isArray(rows)) this.tables[table] = rows;
      }
      return;
    }
    this.seed();
    await this.persistNow();
  }

  persist(): void {
    void this.storage.set(STORAGE_KEY, this.tables);
  }

  private async persistNow(): Promise<void> {
    await this.storage.set(STORAGE_KEY, this.tables);
  }

  storyVisible(storyId: string): boolean {
    const story = this.tables.stories.find((r) => r.id === storyId);
    return Boolean(
      story && (story.published === true || story.author_id === MOCK_USER_ID)
    );
  }

  storyOwned(storyId: string): boolean {
    const story = this.tables.stories.find((r) => r.id === storyId);
    return Boolean(story && story.author_id === MOCK_USER_ID);
  }

  private seed(): void {
    const ts = now();
    const other = "00000000-0000-0000-0000-000000000002";

    this.tables.profiles = [
      {
        id: MOCK_USER_ID,
        display_name: "dev (local mock)",
        created_at: ts,
        updated_at: ts,
      },
      {
        id: other,
        display_name: "Rogue Sysadmin",
        created_at: ts,
        updated_at: ts,
      },
    ];

    const chaos = this.seedCharacter(MOCK_USER_ID, {
      title: "Chaos Pipe",
      role: "pipeline anarchist",
      blurb: "Gets the job done. Sometimes.",
      avatar_emoji: "🐉",
      color: "#ff6b6b",
      traits: ["sarcastic", "unreliable narrator"],
    });
    const gradle = this.seedCharacter(MOCK_USER_ID, {
      title: "Gradle Daemon",
      role: "background process",
      blurb: "Always running. Never sleeps.",
      avatar_emoji: "🤖",
      color: "#feca57",
      traits: ["patient", "forgetful"],
    });
    const oracle = this.seedCharacter(other, {
      title: "Stack Overflow Oracle",
      role: "copy-paste sage",
      blurb: "Will answer anything. Accuracy not guaranteed.",
      avatar_emoji: "🔮",
      color: "#54a0ff",
      traits: ["mysterious"],
    });
    this.tables.characters = [chaos, gradle, oracle];

    const roguelike = this.seedStory(
      MOCK_USER_ID,
      "roguelike",
      "Repo of the Damned",
      "Un-committed work haunts these halls",
      "👹",
      "#8f2d2d",
      true
    );
    const draft = this.seedStory(
      MOCK_USER_ID,
      "linear",
      "Haunted CI YAML",
      "A draft in progress",
      "🎃",
      "#c67b30",
      false
    );
    const theirs = this.seedStory(
      other,
      "linear",
      "The Offboarding Letter",
      "Your exit interview awaits",
      "🚪",
      "#1e3799",
      true
    );
    this.tables.stories = [roguelike, draft, theirs];

    this.tables.story_characters = [
      { story_id: roguelike.id, character_id: chaos.id },
    ];
  }

  private seedCharacter(
    authorId: string,
    fields: {
      title: string;
      role: string;
      blurb: string;
      avatar_emoji: string;
      color: string;
      traits: string[];
    }
  ): MockRow {
    const ts = now();
    return {
      id: newId(),
      author_id: authorId,
      title: fields.title,
      role: fields.role,
      blurb: fields.blurb,
      avatar_emoji: fields.avatar_emoji,
      color: fields.color,
      stats: [{ key: "hp", value: 100 }],
      traits: fields.traits,
      tags: ["seed"],
      public: true,
      created_at: ts,
      updated_at: ts,
    };
  }

  private seedStory(
    authorId: string,
    schema: StorySchema,
    title: string,
    subtitle: string,
    coverEmoji: string,
    coverColor: string,
    published: boolean
  ): MockRow {
    const model = starterModel(schema);
    const report = compileStory(model);
    const ts = now();
    return {
      id: newId(),
      author_id: authorId,
      title,
      subtitle,
      description:
        "Seeded by the dev-mode mock Supabase. Edit or delete me from the Story Studio.",
      cover_emoji: coverEmoji,
      cover_color: coverColor,
      schema,
      model,
      ink_source: report.ink,
      ink_json: report.json.trim().length > 0 ? JSON.parse(report.json) : null,
      published,
      plays: 0,
      created_at: ts,
      updated_at: ts,
    };
  }
}

class Builder {
  private readonly preds: RowFilter[] = [];
  private readonly cols: string[] = [];
  private readonly embeds: { alias: string; table: string; cols: string }[] =
    [];
  private readonly orders: { col: string; desc: boolean }[] = [];
  private maxRows: number | null = null;
  private single = false;
  private op: WriteOp | null = null;

  constructor(
    private readonly dbPromise: Promise<MockDatabase>,
    private readonly table: TableName
  ) {}

  select(spec: string): this {
    const parsed = parseSelect(spec);
    this.cols.push(...parsed.cols);
    this.embeds.push(...parsed.embeds);
    return this;
  }

  eq(col: string, value: unknown): this {
    this.preds.push((r) => r[col] === value);
    return this;
  }

  order(col: string, opts?: { ascending?: boolean }): this {
    this.orders.push({ col, desc: opts?.ascending === false });
    return this;
  }

  limit(n: number): this {
    this.maxRows = n;
    return this;
  }

  maybeSingle(): this {
    this.single = true;
    return this;
  }

  insert(rows: MockRow | MockRow[]): this {
    this.op = { kind: "insert", rows: Array.isArray(rows) ? rows : [rows] };
    return this;
  }

  upsert(
    payload: MockRow | MockRow[],
    opts?: { onConflict?: string; ignoreDuplicates?: boolean }
  ): this {
    this.op = {
      kind: "upsert",
      rows: Array.isArray(payload) ? payload : [payload],
      onConflict: opts?.onConflict ?? "id",
      ignoreDuplicates: Boolean(opts?.ignoreDuplicates),
    };
    return this;
  }

  update(patch: MockRow): this {
    this.op = { kind: "update", patch };
    return this;
  }

  delete(): this {
    this.op = { kind: "delete" };
    return this;
  }

  then<TResult1 = SupabaseResponse, TResult2 = never>(
    onfulfilled?:
      | ((value: SupabaseResponse) => TResult1 | PromiseLike<TResult1>)
      | null
      | undefined,
    onrejected?:
      ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null | undefined
  ): PromiseLike<TResult1 | TResult2> {
    return this.run().then(onfulfilled, onrejected);
  }

  private async run(): Promise<SupabaseResponse> {
    const db = await this.dbPromise;
    try {
      const op = this.op;
      if (op === null) return this.query(db);
      switch (op.kind) {
        case "insert":
          return this.insertRows(db, op.rows);
        case "upsert":
          return this.upsertRows(db);
        case "update":
          return this.updateRows(db, op.patch);
        case "delete":
          return this.deleteRows(db);
      }
    } catch (err) {
      return { data: null, error: mockError(String(err)) };
    }
    return { data: null, error: mockError("unreachable") };
  }

  private query(db: MockDatabase): SupabaseResponse {
    const where = combineFilters(this.rlsSelect(db), ...this.preds);
    let rows = db.tables[this.table].filter(where);
    for (const o of this.orders) {
      rows = rows.slice().sort((a, b) => compareRows(a, b, o.col, o.desc));
    }
    if (this.maxRows !== null) rows = rows.slice(0, this.maxRows);
    const projected = rows.map((r) => this.project(r, db));
    if (this.single) return { data: projected[0] ?? null, error: null };
    return { data: projected, error: null };
  }

  private project(row: MockRow, db: MockDatabase): MockRow {
    const out: MockRow = {};
    const all = this.cols.length === 0 || this.cols.includes("*");
    if (all) {
      Object.assign(out, row);
    } else {
      for (const c of this.cols) if (c in row) out[c] = row[c];
    }
    for (const e of this.embeds) {
      out[e.alias] = resolveEmbed(row, e, db);
    }
    return out;
  }

  private insertRows(db: MockDatabase, rows: MockRow[]): SupabaseResponse {
    const inserted = rows.map((raw) =>
      this.withInsertDefaults(db, { ...raw }, now())
    );
    for (const row of inserted) db.tables[this.table].push(row);
    db.persist();
    return { data: inserted, error: null };
  }

  private upsertRows(db: MockDatabase): SupabaseResponse {
    const op = this.op as Extract<WriteOp, { kind: "upsert" }>;
    const ts = now();
    const out: MockRow[] = [];
    for (const raw of op.rows) {
      const keyValue = raw[op.onConflict];
      const existing =
        keyValue === undefined
          ? undefined
          : db.tables[this.table].find((r) => r[op.onConflict] === keyValue);
      if (existing) {
        if (!op.ignoreDuplicates) {
          const merged: MockRow = { ...existing, ...raw, updated_at: ts };
          if (existing.author_id !== undefined) {
            merged.author_id = existing.author_id;
          }
          Object.assign(existing, merged);
          out.push(existing);
        }
      } else {
        const inserted = this.withInsertDefaults(db, { ...raw }, ts);
        db.tables[this.table].push(inserted);
        out.push(inserted);
      }
    }
    db.persist();
    return { data: out, error: null };
  }

  private updateRows(db: MockDatabase, patch: MockRow): SupabaseResponse {
    const ts = now();
    const allowed = combineFilters(this.rlsWrite(db), ...this.preds);
    const targets = db.tables[this.table].filter(allowed);
    for (const row of targets) {
      Object.assign(row, patch, { updated_at: ts });
    }
    db.persist();
    return { data: targets, error: null };
  }

  private deleteRows(db: MockDatabase): SupabaseResponse {
    const allowed = combineFilters(this.rlsWrite(db), ...this.preds);
    const removed = db.tables[this.table].filter(allowed);
    db.tables[this.table] = db.tables[this.table].filter(
      (r) => !removed.includes(r)
    );
    db.persist();
    return { data: removed, error: null };
  }

  private withInsertDefaults(
    db: MockDatabase,
    row: MockRow,
    ts: string
  ): MockRow {
    if (this.table === "characters" || this.table === "stories") {
      if (row.author_id === undefined) row.author_id = MOCK_USER_ID;
    } else if (this.table === "profiles") {
      if (row.id !== MOCK_USER_ID) {
        throw new Error("profiles: row not owned by this user (RLS)");
      }
    } else if (this.table === "story_characters") {
      const storyId = row.story_id as string | undefined;
      if (!storyId || !db.storyOwned(storyId)) {
        throw new Error("story_characters: denied, story not owned (RLS)");
      }
    }
    if (row.id === undefined) row.id = newId();
    if (row.created_at === undefined) row.created_at = ts;
    row.updated_at = ts;
    return row;
  }

  private rlsSelect(db: MockDatabase): RowFilter {
    switch (this.table) {
      case "profiles":
        return () => true;
      case "characters":
        return (r) => r.public === true || r.author_id === MOCK_USER_ID;
      case "stories":
        return (r) => r.published === true || r.author_id === MOCK_USER_ID;
      case "story_characters":
        return (r) => {
          const storyId = r.story_id as string | undefined;
          return storyId !== undefined && db.storyVisible(storyId);
        };
    }
  }

  private rlsWrite(db: MockDatabase): RowFilter {
    switch (this.table) {
      case "profiles":
        return (r) => r.id === MOCK_USER_ID;
      case "characters":
      case "stories":
        return (r) => r.author_id === MOCK_USER_ID;
      case "story_characters":
        return (r) => {
          const storyId = r.story_id as string | undefined;
          return storyId !== undefined && db.storyOwned(storyId);
        };
    }
  }
}

let dbPromise: Promise<MockDatabase> | null = null;

function getDatabase(): Promise<MockDatabase> {
  dbPromise ??= (async () => {
    const db = new MockDatabase(new StorageService());
    await db.load();
    return db;
  })();
  return dbPromise;
}

const MOCK_SESSION = {
  access_token: "mock-local-access-token",
  refresh_token: "mock-local-refresh-token",
  expires_in: 3600,
  expires_at: Math.floor(Date.now() / 1000) + 3600,
  token_type: "bearer",
  user: {
    id: MOCK_USER_ID,
    aud: "authenticated",
    role: "authenticated",
    email: "dev@local.test",
    email_confirmed_at: null,
    phone: "",
    created_at: now(),
    app_metadata: {},
    user_metadata: {},
    identities: [],
  },
};

export const mockUser: User = MOCK_SESSION.user as unknown as User;

const mockClient = {
  from: (table: string): Builder =>
    new Builder(getDatabase(), table as TableName),
  rpc: (name: string, params: Record<string, unknown>) => {
    const promise = getDatabase().then(async (db) => {
      if (name !== "bump_story_plays") {
        return { data: null, error: mockError(`Unknown rpc: ${name}`) };
      }
      const storyId = params.story_id as string | undefined;
      const story = storyId
        ? db.tables.stories.find((r) => r.id === storyId)
        : undefined;
      if (story) {
        story.plays = Number(story.plays ?? 0) + 1;
        db.persist();
      }
      return { data: null, error: null };
    });
    return {
      then: (resolve?: unknown, reject?: unknown) =>
        promise.then(resolve as never, reject as never),
    };
  },
  auth: {
    getSession: () =>
      Promise.resolve({ data: { session: MOCK_SESSION }, error: null }),
    signInAnonymously: () =>
      Promise.resolve({
        data: { session: MOCK_SESSION, user: MOCK_SESSION.user },
        error: null,
      }),
  },
};

export function createMockClient(): SupabaseClient {
  return mockClient as unknown as SupabaseClient;
}

export function getMockClient(): SupabaseClient | null {
  return isMockEnabled() ? createMockClient() : null;
}
